import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Form, Modal, Button, message, Input, DatePicker, Select, Radio } from 'antd';
import { confirmedAgendaQueryListApi, confirmedlaborInfoApi, improvePersionanlInfoApi } from '@services/SereviseConst';
import { sysdictionaryApi } from '@services/Experts';
import './ConfirmBankInfoModal.less';
import dayjs from 'dayjs';

/**
 * 只读模式下表单字段未通过 Form.Item 注册时，用于「确认劳务信息」按钮可用性校验
 *（与下方各 Form.Item 的 rules 保持一致）
 */
function validateReadOnlyBankInfo(values, currentSelectType, effectiveBankType) {
    if (!values.idcardType) return false;
    if (!values.idCard || String(values.idCard).trim() === '') return false;
    if (!/^[0-9a-z]+$/i.test(String(values.idCard))) return false;

    const birthday = values.birthday;
    if (birthday == null || birthday === '') return false;
    if (dayjs.isDayjs(birthday)) {
        if (!birthday.isValid()) return false;
    } else if (!dayjs(birthday).isValid()) {
        return false;
    }

    if (values.memGender !== 0 && values.memGender !== 1) return false;

    if (currentSelectType !== 1) {
        if (!values.nationality || String(values.nationality).trim() === '') return false;
        if (!values.birthPlace || String(values.birthPlace).trim() === '') return false;
        if (values.bankType !== 1 && values.bankType !== 2) return false;
    }

    const bt = effectiveBankType;
    if (bt === 1) {
        if (!values.bankName || String(values.bankName).trim() === '') return false;
        if (!values.bankCardNo || String(values.bankCardNo).trim() === '') return false;
        if (!/^\d+$/.test(String(values.bankCardNo))) return false;
    } else if (bt === 2) {
        if (!values.payeeUsername || String(values.payeeUsername).trim() === '') return false;
        if (!values.payeePhone || String(values.payeePhone).trim() === '') return false;
        if (!values.payeeAddress || String(values.payeeAddress).trim() === '') return false;
        if (!values.bankName || String(values.bankName).trim() === '') return false;
        if (!values.bankCardNo || String(values.bankCardNo).trim() === '') return false;
        if (!values.swiftBicCode || String(values.swiftBicCode).trim() === '') return false;
    }

    return true;
}

// 选择专家
const ConfirmBankInfoModal = ({ openConfirmBankInfoModal, setOpenConfirmBankInfoModal, expertId, updateList }) => {
    const params = useParams();
    const conferenceId = params?.id || 0; // 会议项目 ID
    const [saveLoading, setSaveLoading] = useState(false); // 保存按钮loading
    const [data, setData] = useState({});
    const [form1] = Form.useForm();
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({ bankCardNo: '', bankName: '', birthPlace: '', birthday: '', idCard: '', idcardType: '', bankType: 1, payeeUsername: '', payeePhone: '', payeeAddress: '', swiftBicCode: '' });
    const [currentSelectType, setCurrentSelectType] = useState(1);
    const [currentSelectTime, setCurrentSelectTime] = useState('');
    const [buttonAble, setButtonAble] = useState(false);
    const [list, setList] = useState([]);
    const [bankType, setBankType] = useState(1); // 1: 境内银行，2: 境外银行

    const formItemLayout = {
        labelCol: {
            xs: { span: 24 },
            sm: { span: 9 },
        },
        wrapperCol: {
            xs: { span: 24 },
            sm: { span: 15 },
        },
    };

    const onCancel = () => {
        setOpenConfirmBankInfoModal(false);
    };
    const onEdit = () => {
        const edit = !isEditing;
        setIsEditing(edit);
    };
    const onSelect = value => {
        setCurrentSelectType(value);
    };
    const setTime = (_, time) => {
        setCurrentSelectTime(time);
    };

    /**
     * 根据身份证号解析出生日期和性别并自动填充
     * 仅当证件类型为身份证（idcardType === 1）且输入为 18 位时触发
     */
    const onIdCardChange = e => {
        const idCardNo = e.target.value?.trim();
        if (currentSelectType !== 1 || !idCardNo || idCardNo.length !== 18) return;

        // 提取出生日期：第 7-14 位，格式 YYYYMMDD
        const birthStr = idCardNo.substring(6, 14);
        const birthMoment = dayjs(birthStr, 'YYYYMMDD', true);
        if (birthMoment.isValid()) {
            const birthDateStr = birthMoment.format('YYYY-MM-DD');
            form1.setFieldValue('birthday', birthMoment);
            setCurrentSelectTime(birthDateStr);
        }

        // 提取性别：第 17 位（索引 16），奇数为男，偶数为女
        const genderCode = parseInt(idCardNo.charAt(16), 10);
        const gender = genderCode % 2 === 1 ? 1 : 0;
        form1.setFieldValue('memGender', gender);
    };
    const checkVail = useCallback(() => {
        // data 由 getList 更新；在回调中引用以配合依赖数组，从而在数据写入表单后重新触发校验
        void data;
        if (isEditing) {
            form1
                .validateFields()
                .then(() => {
                    setButtonAble(false);
                })
                .catch(() => {
                    setButtonAble(true);
                });
            return;
        }
        const values = form1.getFieldsValue(true);
        const effectiveBankType = values.bankType === 1 || values.bankType === 2 ? values.bankType : bankType;
        const ok = validateReadOnlyBankInfo(values, currentSelectType, effectiveBankType);
        setButtonAble(!ok);
    }, [isEditing, form1, currentSelectType, bankType, data]);
    const onConfirm = () => {
        Modal.confirm({
            title: '提示',
            width: 480,
            content: `我已和专家确认劳务信息正确无误，专家已确认不用调整。`,
            centered: true,
            okText: '确认',
            cancelText: '未确认',
            onOk: () => {
                confirmedlaborInfoApi({
                    expertId: expertId,
                    projectId: conferenceId,
                }).then(() => {
                    message.success('操作成功');
                    setOpenConfirmBankInfoModal(false);
                    updateList();
                });
            },
        });
    };
    const onSave = () => {
        setSaveLoading(true);
        form1
            .validateFields()
            .then(values => {
                const newValues = {
                    bankCardNo: values.bankCardNo,
                    birthday: currentSelectTime,
                    birthPlace: values.birthPlace,
                    idCard: values.idCard,
                    idcardType: values.idcardType,
                    expertId: expertId,
                    conferenceId: conferenceId,
                    nationality: values.nationality,
                    memGender: values.memGender,
                    bankType: values.bankType,
                    payeeUsername: values.payeeUsername,
                    payeePhone: values.payeePhone,
                    payeeAddress: values.payeeAddress,
                    bankName: values.bankName,
                    swiftBicCode: values.swiftBicCode,
                };
                improvePersionanlInfoApi(newValues)
                    .then(res => {
                        setSaveLoading(false);
                        if (res && res.status_code === 200) {
                            message.success('专家信息保存成功');
                            setFormData(newValues);
                            setIsEditing(false);
                            updateList();
                        }
                    })
                    .catch(() => {
                        setSaveLoading(false);
                    });
            })
            .catch(() => {
                setSaveLoading(false);
            });
    };
    // 劳务费列表
    const getList = async () => {
        const res = await confirmedAgendaQueryListApi({
            conferenceId: conferenceId,
            expertId: expertId,
        });
        if (res.status_code === 200) {
            setData(res.data);
            form1.setFieldValue('bankCardNo', res.data.bankCardNo);
            form1.setFieldValue('bankName', res.data.bankName);
            const time = res.data.birthday ? dayjs(res.data.birthday, 'YYYY-MM-DD') : null;
            form1.setFieldValue('birthday', time);
            form1.setFieldValue('birthPlace', res.data.birthPlace);
            form1.setFieldValue('idCard', res.data.idCard);
            form1.setFieldValue('idcardType', res.data.idcardType);
            form1.setFieldValue('nationality', res.data.nationality);
            form1.setFieldValue('memGender', res.data.memGender);
            setCurrentSelectType(res.data.idcardType);
            setCurrentSelectTime(res.data.birthday);

            form1.setFieldValue('bankType', res.data.bankType || 1);
            form1.setFieldValue('payeeUsername', res.data.payeeUsername);
            form1.setFieldValue('payeePhone', res.data.payeePhone);
            form1.setFieldValue('payeeAddress', res.data.payeeAddress);
            form1.setFieldValue('bankName', res.data.bankName);
            form1.setFieldValue('swiftBicCode', res.data.swiftBicCode);
            form1.setFieldValue('bankType', res.data.bankType || 1);

            setBankType(res.data.bankType || 1);

            setFormData({
                bankCardNo: res.data.bankCardNo,
                birthday: res.data.birthday,
                birthPlace: res.data.birthPlace,
                idCard: res.data.idCard,
                idcardType: res.data.idcardType,
                nationality: res.data.nationality,
                memGender: res.data.memGender,
                bankType: res.data.bankType || 1,
                payeeUsername: res.data.payeeUsername,
                payeePhone: res.data.payeePhone,
                payeeAddress: res.data.payeeAddress,
                bankName: res.data.bankName,
                swiftBicCode: res.data.swiftBicCode,
            });
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            checkVail();
        }, 500);
        return () => clearTimeout(timer);
    }, [checkVail]);

    useEffect(() => {
        if (expertId > 0 && openConfirmBankInfoModal) {
            setIsEditing(false);
            getList();
            sysdictionaryApi({ dicGroup: 'NATIONALITY' }).then(res => {
                if (res.status_code === 200 && res.data && res.data.length) {
                    setList(res.data);
                }
            });
        } else {
            form1.resetFields();
            setCurrentSelectTime('');
            setBankType(1);
        }
    }, [openConfirmBankInfoModal]);

    return (
        <Modal
            className="bankInfo"
            title="确认收款信息"
            open={openConfirmBankInfoModal}
            centered
            width={600}
            onCancel={onCancel}
            destroyOnHidden={true}
            footer={[
                !isEditing && (
                    <Button key="confirm-labor" disabled={buttonAble} type="primary" onClick={onConfirm}>
                        确认劳务信息
                    </Button>
                ),
                isEditing && (
                    <Button key="submit" type="primary" loading={saveLoading} onClick={onSave}>
                        保存
                    </Button>
                ),
            ]}
        >
            {!isEditing && (
                <Button className="editButton" type="link" onClick={onEdit}>
                    编辑
                </Button>
            )}

            <Form {...formItemLayout} form={form1}>
                <Form.Item label="专家姓名">
                    <span>{data.username}</span>
                </Form.Item>
                <Form.Item label="证件类型" {...(isEditing ? { name: 'idcardType', rules: [{ required: true, message: isEditing ? '请选择证件类型' : '' }] } : {})}>
                    {isEditing ? (
                        <Select placeholder="请选择证件类型" onSelect={onSelect}>
                            <Select.Option value={1}>身份证</Select.Option>
                            <Select.Option value={2}>港澳台来往内地通行证</Select.Option>
                            <Select.Option value={3}>台湾居民来往大陆通行证</Select.Option>
                            <Select.Option value={4}>外国人永久居留身份证</Select.Option>
                            <Select.Option value={5}>护照</Select.Option>
                        </Select>
                    ) : (
                        <>
                            {formData.idcardType === 1 && <span>身份证</span>}
                            {formData.idcardType === 2 && <span>港澳台来往内地通行证</span>}
                            {formData.idcardType === 3 && <span>台湾居民来往大陆通行证</span>}
                            {formData.idcardType === 4 && <span>外国人永久居留身份证</span>}
                            {formData.idcardType === 5 && <span>护照</span>}
                            {formData.idcardType === null && <span className="placeholderColor">&ndash;</span>}
                        </>
                    )}
                </Form.Item>

                <Form.Item
                    label="证件号"
                    {...(isEditing
                        ? {
                              name: 'idCard',
                              rules: [
                                  { required: true, message: isEditing ? '请填写证件号码' : '' },
                                  { pattern: /^[0-9a-z]+$/i, message: '证件号格式不正确' },
                              ],
                          }
                        : {})}
                >
                    {isEditing ? (
                        <Input allowClear placeholder="请输入证件号码" onChange={onIdCardChange} />
                    ) : (
                        <>
                            {formData.idCard && <span>{formData.idCard}</span>}
                            {!formData.idCard && <span className="placeholderColor">&ndash;</span>}
                        </>
                    )}
                </Form.Item>

                {/* {currentSelectType !== 1 && ( */}
                <Form.Item label="出生日期" {...(isEditing ? { name: 'birthday', rules: [{ required: true, message: isEditing ? '请选择出生日期' : '' }] } : {})}>
                    {isEditing ? (
                        <DatePicker style={{ width: '100%' }} format={'YYYY-MM-DD'} onChange={setTime} />
                    ) : (
                        <>
                            {formData.birthday && <span>{formData.birthday}</span>}
                            {!formData.birthday && <span className="placeholderColor">&ndash;</span>}
                        </>
                    )}
                </Form.Item>
                {/* )} */}

                <Form.Item label="性别" {...(isEditing ? { name: 'memGender', rules: [{ required: true, message: isEditing ? '请选择性别' : '' }] } : {})}>
                    {isEditing ? (
                        <Radio.Group>
                            <Radio value={1}>男</Radio>
                            <Radio value={0}>女</Radio>
                        </Radio.Group>
                    ) : (
                        <>
                            {formData.memGender === 1 && <span>男</span>}
                            {formData.memGender === 0 && <span>女</span>}
                            {formData.memGender === null && <span className="placeholderColor">&ndash;</span>}
                        </>
                    )}
                </Form.Item>

                {currentSelectType !== 1 && (
                    <Form.Item
                        label="国籍"
                        {...(isEditing
                            ? {
                                  name: 'nationality',
                                  rules: [{ required: currentSelectType !== 1, message: isEditing ? '请填写国籍' : '' }],
                              }
                            : {})}
                    >
                        {isEditing ? (
                            <Select fieldNames={{ label: 'dicName', value: 'dicName' }} options={list} placeholder="请选择国籍" showSearch={{ filterOption: (input, option) => (option?.dicName ?? '').includes(input) }} />
                        ) : (
                            <>
                                {formData.nationality && <span>{formData.nationality}</span>}
                                {!formData.nationality && <span className="placeholderColor">&ndash;</span>}
                            </>
                        )}
                    </Form.Item>
                )}

                {currentSelectType !== 1 && (
                    <Form.Item
                        label="出生地"
                        {...(isEditing
                            ? {
                                  name: 'birthPlace',
                                  rules: [{ required: currentSelectType !== 1, message: isEditing ? '请填写出生地' : '' }],
                              }
                            : {})}
                    >
                        {isEditing ? (
                            <Input allowClear placeholder="请输入出生地" />
                        ) : (
                            <>
                                {formData.birthPlace && <span>{formData.birthPlace}</span>}
                                {!formData.birthPlace && <span className="placeholderColor">&ndash;</span>}
                            </>
                        )}
                    </Form.Item>
                )}

                {currentSelectType !== 1 && (
                    <Form.Item label="银行账户类型" {...(isEditing ? { name: 'bankType', rules: [{ required: true, message: isEditing ? '请选择银行账户类型' : '' }] } : {})}>
                        {isEditing ? (
                            <Radio.Group onChange={e => setBankType(e.target.value)}>
                                <Radio value={1}>境内银行账户</Radio>
                                <Radio value={2}>境外银行账户</Radio>
                            </Radio.Group>
                        ) : (
                            <>
                                {bankType === 1 && <span>境内银行账户</span>}
                                {bankType === 2 && <span>境外银行账户</span>}
                                {!bankType && <span className="placeholderColor">&ndash;</span>}
                            </>
                        )}
                    </Form.Item>
                )}

                {bankType === 1 && (
                    <>
                        <Form.Item label="开户银行" {...(isEditing ? { name: 'bankName', rules: [{ required: true, message: isEditing ? '请填写开户银行' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入开户行名称" />
                            ) : (
                                <>
                                    {formData.bankName && <span>{formData.bankName}</span>}
                                    {!formData.bankName && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item
                            label="银行账号"
                            {...(isEditing
                                ? {
                                      name: 'bankCardNo',
                                      rules: [
                                          { required: true, message: isEditing ? '请输入银行账号' : '' },
                                          { pattern: /^\d+$/i, message: '银行账号格式不正确' },
                                      ],
                                  }
                                : {})}
                        >
                            {isEditing ? (
                                <Input allowClear placeholder="请输入银行账号" />
                            ) : (
                                <>
                                    {formData.bankCardNo && <span>{formData.bankCardNo}</span>}
                                    {!formData.bankCardNo && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                    </>
                )}

                {bankType === 2 && (
                    <>
                        <Form.Item label="收款人名称(英文)" {...(isEditing ? { name: 'payeeUsername', rules: [{ required: true, message: isEditing ? '请填写收款人名称' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入收款人名称(英文)" />
                            ) : (
                                <>
                                    {formData.payeeUsername && <span>{formData.payeeUsername}</span>}
                                    {!formData.payeeUsername && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item label="收款人银行预留电话" {...(isEditing ? { name: 'payeePhone', rules: [{ required: true, message: isEditing ? '请填写银行预留电话' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入收益人银行预留电话" />
                            ) : (
                                <>
                                    {formData.payeePhone && <span>{formData.payeePhone}</span>}
                                    {!formData.payeePhone && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item label="收款人境外汇款住址(英文)" {...(isEditing ? { name: 'payeeAddress', rules: [{ required: true, message: isEditing ? '请填写汇款住址' : '' }] } : {})}>
                            {isEditing ? (
                                <Input.TextArea allowClear placeholder="请输入收款人境外汇款住址(英文)" rows={2} />
                            ) : (
                                <>
                                    {formData.payeeAddress && <span>{formData.payeeAddress}</span>}
                                    {!formData.payeeAddress && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item label="开户银行名称(英文)" {...(isEditing ? { name: 'bankName', rules: [{ required: true, message: isEditing ? '请填写开户银行名称' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入开户银行名称(英文)" />
                            ) : (
                                <>
                                    {formData.bankName && <span>{formData.bankName}</span>}
                                    {!formData.bankName && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item label="银行账号" {...(isEditing ? { name: 'bankCardNo', rules: [{ required: true, message: isEditing ? '请输入银行账号' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入银行账号" />
                            ) : (
                                <>
                                    {formData.bankCardNo && <span>{formData.bankCardNo}</span>}
                                    {!formData.bankCardNo && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                        <Form.Item label="SWIFT/BIC Code" {...(isEditing ? { name: 'swiftBicCode', rules: [{ required: true, message: isEditing ? '请填写SWIFT/BIC Code' : '' }] } : {})}>
                            {isEditing ? (
                                <Input allowClear placeholder="请输入SWIFT/BIC Code" />
                            ) : (
                                <>
                                    {formData.swiftBicCode && <span>{formData.swiftBicCode}</span>}
                                    {!formData.swiftBicCode && <span className="placeholderColor">&ndash;</span>}
                                </>
                            )}
                        </Form.Item>
                    </>
                )}
            </Form>
        </Modal>
    );
};

export default ConfirmBankInfoModal;
